/**
 * Wall Geometry Utilities
 *
 * Provides algorithms for:
 * - Wall click/hit detection using point-to-line-segment distance
 * - Offset calculation along walls for door/window placement
 * - Validation for element placement on walls
 *
 * Based on vector projection mathematics that works for walls at any angle.
 */

import type { Point2D, WallSegment, StructuralElement } from "@byk/shared";

/**
 * Result of a wall hit detection
 */
export interface WallHitResult {
  wallId: string;
  wall: WallSegment;
  /** Normalized position along wall (0 to 1) */
  t: number;
  /** Distance from wall start in cm */
  offset: number;
  /** Closest point on wall to the click */
  point: Point2D;
  /** Distance from click to wall in pixels */
  distance: number;
  /** Wall length in cm */
  wallLength: number;
  /** Wall angle in radians */
  angle: number;
}

/**
 * Default dimensions for structural elements (in cm)
 */
export const ELEMENT_DEFAULTS = {
  door: {
    width: 90, // Standard door width
    height: 210, // Standard door height
    elevation: 0, // Floor level
  },
  door_double: {
    width: 180,
    height: 210,
    elevation: 0,
  },
  window: {
    width: 120, // Standard window width
    height: 120, // Standard window height
    elevation: 90, // Standard sill height (counter height)
  },
  opening: {
    width: 100,
    height: 210,
    elevation: 0,
  },
} as const;

/**
 * Minimum distance from wall corners for element placement (in cm)
 */
export const CORNER_PADDING = 10;

/**
 * Calculate the distance from a point to a line segment and the closest point.
 * Uses vector projection for accuracy at any wall angle.
 *
 * @param px - Point X coordinate
 * @param py - Point Y coordinate
 * @param x1 - Wall start X
 * @param y1 - Wall start Y
 * @param x2 - Wall end X
 * @param y2 - Wall end Y
 * @returns Object with distance, t (0-1 normalized position), and closest point
 */
export function pointToSegmentDistance(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): { distance: number; t: number; closestX: number; closestY: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;

  // Handle zero-length segment
  if (lengthSq === 0) {
    const dist = Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
    return { distance: dist, t: 0, closestX: x1, closestY: y1 };
  }

  // Project point onto line (parameter t between 0-1)
  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;

  // Clamp t to segment bounds [0, 1]
  t = Math.max(0, Math.min(1, t));

  // Calculate closest point on segment
  const closestX = x1 + t * dx;
  const closestY = y1 + t * dy;

  // Calculate distance from point to closest point on segment
  const distance = Math.sqrt((px - closestX) ** 2 + (py - closestY) ** 2);

  return { distance, t, closestX, closestY };
}

/**
 * Detect if a click point hits a wall within tolerance.
 *
 * @param clickX - Click X coordinate in room units (cm)
 * @param clickY - Click Y coordinate in room units (cm)
 * @param walls - Array of wall segments to check
 * @param toleranceCm - Maximum distance from wall to count as hit (in cm)
 * @returns WallHitResult if a wall was hit, null otherwise
 */
export function detectWallHit(
  clickX: number,
  clickY: number,
  walls: WallSegment[],
  toleranceCm: number = 15
): WallHitResult | null {
  let bestHit: WallHitResult | null = null;
  let bestDistance = Infinity;

  for (const wall of walls) {
    const result = pointToSegmentDistance(
      clickX,
      clickY,
      wall.start.x,
      wall.start.y,
      wall.end.x,
      wall.end.y
    );

    if (result.distance <= toleranceCm && result.distance < bestDistance) {
      const wallLength = Math.sqrt(
        (wall.end.x - wall.start.x) ** 2 + (wall.end.y - wall.start.y) ** 2
      );
      const angle = Math.atan2(
        wall.end.y - wall.start.y,
        wall.end.x - wall.start.x
      );

      bestHit = {
        wallId: wall.id,
        wall,
        t: result.t,
        offset: result.t * wallLength,
        point: { x: result.closestX, y: result.closestY },
        distance: result.distance,
        wallLength,
        angle,
      };
      bestDistance = result.distance;
    }
  }

  return bestHit;
}

/**
 * Validate if an element can be placed at the given offset on a wall.
 *
 * @param wall - The wall segment
 * @param offset - Offset from wall start in cm
 * @param elementWidth - Width of element in cm
 * @param existingElements - Existing elements on this wall
 * @returns Object with valid flag and reason if invalid
 */
export function validateElementPlacement(
  wall: WallSegment,
  offset: number,
  elementWidth: number,
  existingElements: StructuralElement[]
): { valid: boolean; reason?: string } {
  const wallLength = Math.sqrt(
    (wall.end.x - wall.start.x) ** 2 + (wall.end.y - wall.start.y) ** 2
  );

  const halfWidth = elementWidth / 2;
  const elementStart = offset - halfWidth;
  const elementEnd = offset + halfWidth;

  // Check: Element fits within wall bounds
  if (elementStart < 0) {
    return { valid: false, reason: "Element extends past wall start" };
  }
  if (elementEnd > wallLength) {
    return { valid: false, reason: "Element extends past wall end" };
  }

  // Check: Element is not too close to corners
  if (elementStart < CORNER_PADDING) {
    return {
      valid: false,
      reason: `Element must be at least ${CORNER_PADDING}cm from wall start`,
    };
  }
  if (elementEnd > wallLength - CORNER_PADDING) {
    return {
      valid: false,
      reason: `Element must be at least ${CORNER_PADDING}cm from wall end`,
    };
  }

  // Check: No overlap with existing elements on same wall
  const elementsOnWall = existingElements.filter((e) => e.wallId === wall.id);

  for (const existing of elementsOnWall) {
    const existingStart = existing.offset - existing.width / 2;
    const existingEnd = existing.offset + existing.width / 2;

    // Interval overlap check: A overlaps B if A.start < B.end AND A.end > B.start
    if (elementStart < existingEnd && elementEnd > existingStart) {
      return {
        valid: false,
        reason: `Overlaps with existing ${existing.type}`,
      };
    }
  }

  return { valid: true };
}

/**
 * Calculate the position and rotation for rendering an element on a wall.
 *
 * @param wall - The wall segment
 * @param offset - Offset from wall start in cm
 * @returns Position (x, y) and angle in degrees
 */
export function getElementRenderPosition(
  wall: WallSegment,
  offset: number
): { x: number; y: number; angle: number } {
  const wallLength = Math.sqrt(
    (wall.end.x - wall.start.x) ** 2 + (wall.end.y - wall.start.y) ** 2
  );

  // Clamp offset to valid range
  const t = Math.max(0, Math.min(1, offset / wallLength));

  const x = wall.start.x + (wall.end.x - wall.start.x) * t;
  const y = wall.start.y + (wall.end.y - wall.start.y) * t;

  const angleRad = Math.atan2(
    wall.end.y - wall.start.y,
    wall.end.x - wall.start.x
  );
  const angleDeg = angleRad * (180 / Math.PI);

  return { x, y, angle: angleDeg };
}

/**
 * Snap offset to grid if close enough.
 *
 * @param offset - Raw offset in cm
 * @param gridSize - Grid size in cm
 * @param snapThreshold - Threshold for snapping in cm
 * @returns Snapped offset
 */
export function snapOffsetToGrid(
  offset: number,
  gridSize: number = 10,
  snapThreshold: number = 5
): number {
  const nearestGrid = Math.round(offset / gridSize) * gridSize;
  if (Math.abs(offset - nearestGrid) <= snapThreshold) {
    return nearestGrid;
  }
  return offset;
}

/**
 * Calculate constrained offset when dragging an element along its wall.
 * Ensures the element stays within valid bounds.
 *
 * @param wall - The wall segment
 * @param mouseX - Mouse X in room coordinates
 * @param mouseY - Mouse Y in room coordinates
 * @param elementWidth - Width of the element
 * @returns Constrained offset
 */
export function calculateConstrainedOffset(
  wall: WallSegment,
  mouseX: number,
  mouseY: number,
  elementWidth: number
): number {
  // Project mouse position onto wall
  const result = pointToSegmentDistance(
    mouseX,
    mouseY,
    wall.start.x,
    wall.start.y,
    wall.end.x,
    wall.end.y
  );

  const wallLength = Math.sqrt(
    (wall.end.x - wall.start.x) ** 2 + (wall.end.y - wall.start.y) ** 2
  );

  const rawOffset = result.t * wallLength;
  const halfWidth = elementWidth / 2;

  // Constrain offset so element stays within wall bounds with corner padding
  const minOffset = CORNER_PADDING + halfWidth;
  const maxOffset = wallLength - CORNER_PADDING - halfWidth;

  return Math.max(minOffset, Math.min(maxOffset, rawOffset));
}
